import React, {
    FC,
    ReactNode,
    useMemo,
    useState,
    createContext,
    useContext, useCallback, useEffect,
} from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { toggleLoading } from '../../store/ducks/pageConfig';
import { ThesaurusItemDetails } from '../../modules/consulta/components/SearchBar/thesaurus/types/ThesaurusItemDetails';
import * as ThesaurusService from '../services/ThesaurusService';
import { APIHelper } from '../helpers';

interface ThesaurusContextProps {
    searchData: any
    changeSearchResults: (data: any) => void
    changeLegacyLoading:  (loading: boolean) => void
    searchOption:any,
    changeSearchOption: (searchOption: any) => void
    searchTherm:string|null
    changeSearchTherm: (searchTherm: string|null) => void;
    itemDetails: Array<Array<ThesaurusItemDetails>>
    changeItemDetails : ( itemDetails: Array<Array<ThesaurusItemDetails>>) => void
    searchType:'term'|'tree',
    changeSearchType: (searchType: 'term'|'tree') => void
    openAll: boolean|undefined
    changeOpenAll: (openAll: boolean|undefined) => void
    hasSearched: boolean;
    search: (data: { searchTerm: string; searchOption: string }) => Promise<void>;
}

const ThesaurusContext = createContext<ThesaurusContextProps | undefined>(undefined);

interface ThesaurusContextProviderProps {
    children: ReactNode;
}

const ThesaurusContextProvider: FC<ThesaurusContextProviderProps> = ({ children }) => {
    const [searchData, setSearchData] = useState<any>(undefined);
    const [itemsChildren, setItemsChildren] = useState<any>(undefined);
    const [searchOption, setSearchOption] = useState(-1)
    const dispatch = useDispatch()
    const [searchTherm, setSearchTherm] = useState<string|null>(null)
    const [itemDetails, setItemDetails] = useState<Array<Array<ThesaurusItemDetails>>>([])
    const [searchType, setSearchType] = useState<'term'|'tree'>('term')
    const [openAll, setOpenAll] = useState<boolean|undefined>()
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    
    const changeSearchResults = useCallback((searchData : any) => setSearchData(searchData) , [])
    const changeOpenAll = (openAll: boolean|undefined) => setOpenAll(openAll)
    const changeLegacyLoading = useCallback((loading: boolean) => dispatch(toggleLoading(loading)), [dispatch])
    const changeSearchOption = useCallback((searchOption: any) => setSearchOption(searchOption), [])
    const changeSearchType = (searchType: 'term'|'tree') => setSearchType(searchType)
    const changeSearchTherm = (searchTherm: string|null)=> setSearchTherm(searchTherm)
    const changeItemDetails = ( itemDetails: Array<Array<ThesaurusItemDetails>>) => setItemDetails(itemDetails)

    const search = useCallback(async (data: { searchTerm: string; searchOption: string }) => {
        setSearchData(undefined);
        setHasSearched(false);
        changeLegacyLoading(true);
        ThesaurusService.getThesaurusSearch(
            data.searchTerm,
            data.searchOption,
        )
        .then(response => {
            const { data } = response;
            if (data) {
                setSearchData(data);
            }
        })
        .catch(error => {
            if (!axios.isCancel(error)) {
                APIHelper.tratarErro(error);
            }
        })
        .finally(() => {
            changeLegacyLoading(false);
            setHasSearched(true);
        });
    }, []);

    const memoizedData: ThesaurusContextProps = useMemo(
        () => ({
            searchData,
            changeSearchResults,
            changeLegacyLoading,
            searchOption,
            changeSearchOption,
            searchTherm,
            changeSearchTherm,
            itemDetails,
            changeItemDetails,
            searchType,
            changeSearchType,
            openAll,
            changeOpenAll,
            hasSearched,
            search,
        }),
        [
            searchData,
            changeSearchResults,
            searchOption,
            searchTherm,
            itemDetails,
            searchType,
            openAll,
            hasSearched,
        ],
    );

    return (
        <ThesaurusContext.Provider value={memoizedData}>
            {children}
        </ThesaurusContext.Provider>
    );
};

export const useThesaurusContext = (): ThesaurusContextProps => {
    const context = useContext(ThesaurusContext) as ThesaurusContextProps;
    if (context === undefined)
        throw new Error(
            'useThesaurusContext must be used within a ThesaurusContextProvider',
        );
    return context;
};

export default ThesaurusContextProvider;
